import { Check, Minus, X, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils.js";
import {
  verdictLabel,
  type VerdictRating,
} from "@/components/experience/vocabulary";

export type { VerdictRating };

interface VerdictIndicatorProps {
  rating: VerdictRating;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const ratingConfig: Record<
  VerdictRating,
  {
    bg: string;
    text: string;
    pill: string;
    icon: typeof Check;
  }
> = {
  yes: {
    bg: "bg-mark-helps/10",
    text: "text-finding-helps",
    pill: "bg-transparent text-finding-helps border border-finding-helps/30",
    icon: Check,
  },
  partially: {
    bg: "bg-mark-important/10",
    text: "text-severity-important",
    pill: "bg-transparent text-severity-important border border-severity-important/30",
    icon: AlertTriangle,
  },
  no: {
    bg: "bg-mark-critical/10",
    text: "text-severity-critical",
    pill: "bg-transparent text-severity-critical border border-severity-critical/30",
    icon: X,
  },
  not_applicable: {
    bg: "bg-muted",
    text: "text-muted-foreground",
    pill: "bg-transparent text-muted-foreground border border-border",
    icon: Minus,
  },
};

const sizeClasses = {
  sm: "size-6 [&>svg]:size-3",
  md: "size-9 [&>svg]:size-4.5",
  lg: "size-11 [&>svg]:size-5",
} as const;

/**
 * Colored circle with icon representing a walkthrough verdict.
 * Maps the Human Test answer (yes/partially/no) to a visual signal.
 */
export function VerdictIndicator({
  rating,
  size = "md",
  className,
}: VerdictIndicatorProps) {
  const config = ratingConfig[rating];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full",
        config.bg,
        config.text,
        sizeClasses[size],
        className,
      )}
      role="img"
      aria-label={`Verdict: ${verdictLabel(rating)}`}
    >
      <Icon strokeWidth={2.5} />
    </div>
  );
}

/** Colored pill showing the verdict label (e.g., green "Yes", amber "Partial") */
export function VerdictPill({ rating }: { rating: VerdictRating }) {
  const config = ratingConfig[rating];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
        config.pill,
      )}
    >
      {verdictLabel(rating)}
    </span>
  );
}
